import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiPieChart, FiTrendingUp, FiShield } from 'react-icons/fi';
import { reportData } from '../data/reportData';
import SEO from '../components/SEO';
import CTABlock from '../components/CTABlock';

export default function Home() {
  const { global, sections } = reportData;

  return (
    <div className="bg-slate-50">
      <SEO title={global.title} description={global.subtitle} />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-peridot-100 text-peridot-900 text-sm font-semibold mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-peridot-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-peridot-600"></span>
            </span>
            New Research Data
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-6xl font-extrabold text-slate-900 leading-tight mb-6">
            {global.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl text-slate-600 mb-8 leading-relaxed">
            {global.subtitle}. We surveyed 400 IT and Security leaders to uncover the reality of unmanaged AI adoption in the enterprise. The findings reveal a massive governance gap.
          </motion.p>
        </div>

        {/* Key Stats Grid */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {global.keyStats.map((stat, idx) => (
            <div key={idx} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <div className="text-4xl font-black text-slate-900 mb-2">{stat.value}</div>
              <div className="text-slate-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Sections Grid */}
      <section className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center">Explore the Findings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, idx) => (
              <Link key={section.id} to={`/${section.slug}`} className="group block h-full">
                <div className="h-full border border-slate-200 rounded-2xl p-8 hover:border-slate-400 hover:shadow-md transition-all bg-slate-50 hover:bg-white flex flex-col">
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-peridot-600 transition-colors">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-6 flex-grow leading-relaxed">
                    {section.description}
                  </p>
                  <div className="flex justify-between items-center text-sm font-semibold text-slate-900 mt-auto pt-4 border-t border-slate-200">
                    <span>{section.questions.length} Insights</span>
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform text-peridot-600" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <CTABlock variant="dark" />
      </section>
    </div>
  );
}